import React, { useState, useEffect } from "react";
import Dropdown from "../../../components/Dropdown";
import { LineChart, ChartsGrid } from '@mui/x-charts';
import { getNewsViewHistoryChart } from "../../../reducers/companyContentManage"
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import DateRangePicker from "../../../components/DateRangePicker";
import { getXAxisConfigNewsViewChart, formatXAxisTooltip, formatXAxisLabelNewsChart, getMarkElementStyle } from "../../../utils/formatChart";

const annotation = [
  {
    name: "全体",
    color: "bg-alu-primary",
  },
  {
    name: "採用ランクA",
    color: "bg-alu-dot",
  },
];

const ViewingRateChart = () => {
  const [selected, setSelected] = useState({ name: "過去30日間", id: 3 });
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const {
    loading,
    success,
    error,
    chartData,
    isValidChart,
  } = useSelector((state: any) => state.companyContentManage);
  const specialOptions = [6, 7];

  const handleSelectedTime = (value: any) => {
    setSelected(value);
    return;
  }

  useEffect(() => {
    const data = {
      option: selected.id,
    };
    dispatch(getNewsViewHistoryChart(data));
  }, [selected.id]);
  
  const { pData, uData, xLabels } = chartData;

  const config = getXAxisConfigNewsViewChart(xLabels, selected?.id, 8);

  const safeMax = (arr: (number | null | undefined)[] = []) => {
    const nums = arr.filter((v): v is number => typeof v === 'number');
    return nums.length > 0 ? Math.max(...nums) : 0;
  };

  const leftMax = safeMax([
    ...pData,
    ...uData,
  ]);

  const leftYAxis =
    leftMax === 0
      ? {
          min: 0,
          max: 1,
          interval: 1,
          alignTicks: true,
        }
      : {
          min: 0,
          alignTicks: true,
        };
        
  return (
    <>
      <div className="flex items-center justify-between gap-6 mb-4 pl-4">
        <div className="flex items-center gap-6 flex-1">
          <h3 className="font-bold text-base alu-text-base-2">閲覧数</h3>
          <div className="flex items-center gap-4">
            {annotation.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1">
                <span className={`w-3 h-3 rounded ${item.color}`}></span>
                <p className="text-sm leading-[1.3125rem] alu-text-base-1">
                  {item.name}
                </p>
              </span>
            ))}
          </div>
        </div>
        <div className="shrink-0 min-w-[160px]">
          <Dropdown
            inputWidth="full"
            dropdownPosition={"right"}
            optionsAnchor={"right"}
            type={"text"}
            options={[
              { name: '今月', id: 1 },
              { name: '先月', id: 2 },
              { name: '過去30日間', id: 3 },
              { name: '過去90日間', id: 4 },
              { name: '過去1年間', id: 5 },
              { name: '全期間（週）', id: 6 },
              { name: '全期間（月）', id: 7 },
            ]}
            value={selected}
            onChange={(value: any) => handleSelectedTime(value)}
          />
        </div>
      </div>
      <div className="w-[450px] h-[250px] bg-white rounded-2xl alu-section-box-shadow">
        {!isValidChart ? 
          <div className="w-full h-full flex justify-center items-center">
            <span className="text-sm leading-[1.3125rem] font-normal alu-text-base-1">まだデータがありません</span>
          </div> :
          <LineChart
            width={450}
            height={250}
            series={[
              { id: 'view', data: pData, label: '全体', color: '#FF861A',curve: "linear", showMark: pData.filter(v => v !== null).length === 1 ? true : false },
              { id: 'view-rank-a', data: uData, label: '採用ランクA', color: '#1D70D4', curve: "linear", showMark: uData.filter(v => v !== null).length === 1 ? true : false },
            ]}
            xAxis={[
              { 
                scaleType: 'band',
                data: xLabels,
                tickValues: config.tickValues, 
                tickInterval: config.tickValues,
                tickFontSize: 12,
                tickLabelStyle: { fill: "#808080" },
                disableLine: true,
                disableTicks: true,
                valueFormatter: (value, context) => {
                  const index = xLabels.indexOf(value);
                  if (context?.location === 'tooltip') {
                    return formatXAxisTooltip(value, index, selected?.id);
                  }
                  return formatXAxisLabelNewsChart(value, index, config, selected?.id, xLabels);
                }
              }
            ]}
            yAxis={[
              { 
                tickFontSize: 10,
                tickLabelStyle: {
                  fill:"#CBD5E0",
                  fontWeight: 700,
                },
                disableLine: true,
                disableTicks: true,
                tickMinStep: 1,
                valueFormatter: (value, context) => {
                  if (!context || context.location === 'tooltip') {
                    return value !== null ? Math.round(value) : '';
                  }

                  if (leftMax === 0 && value === 1) return ''

                  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                  return value;
                },
                ...leftYAxis,
              }
            ]}
            slotProps={{
              legend: { hidden: true, },
            }}
            sx={{
              '& .MuiMarkElement-series-view-rank-a': getMarkElementStyle('#1D70D4', xLabels.length, false),
              '& .MuiMarkElement-series-view': getMarkElementStyle('#FF861A', xLabels.length, false),
              '& .MuiMarkElement-root': { scale: '0.6' },
            }}
          >
          <ChartsGrid 
            horizontal stroke="#888" 
            strokeDasharray="5 5" 
            opacity={0.6}
          />
          </LineChart>
        }
      </div>
    </>
  );
};

export default ViewingRateChart;
